import { forwardRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';

type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
  className?: string;
  /** Renders the error treatment. Pair with aria-invalid on the caller. */
  invalid?: boolean;
};

/**
 * A multi-line field that matches base/Input exactly: white surface, green
 * hairline, the one brand focus ring, and 16px text on phones so iOS does not
 * zoom into the field. See Apps/web/THEME.md.
 */
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className = '', invalid = false, rows = 4, ...rest },
  ref
) {
  const base =
    'block w-full resize-y rounded-xl border bg-white px-4 py-3 sm:py-2.5 text-base sm:text-sm leading-relaxed text-[var(--pv-color-text)] ' +
    'placeholder:text-[var(--pv-color-text-subtle)] outline-none transition-colors duration-200 ' +
    'disabled:cursor-not-allowed disabled:bg-[var(--pv-color-surface-soft)] disabled:text-[var(--pv-color-text-subtle)] ' +
    'dark:bg-[#08372F] dark:text-[#E6F3EF] dark:placeholder:text-[#6A8480]';

  const state = invalid
    ? 'border-[#DF2F2B] focus:border-[#DF2F2B] focus:ring-2 focus:ring-[#DF2F2B]/20'
    : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-accent-soft)] focus:border-[#004D40] focus:ring-2 focus:ring-[#004D40]/20 ' +
      'dark:border-white/15 dark:hover:border-white/25 dark:focus:border-[#3FBBA2] dark:focus:ring-[#3FBBA2]/25';

  return <textarea ref={ref} rows={rows} {...rest} className={`${base} ${state} ${className}`.trim()} />;
});

export default TextArea;
